import React, { useEffect, useState, useRef, useCallback } from "react";
import { Button, message, Form, Input, Upload, Modal, Select, Tabs } from "antd";
import type { RcFile, UploadFile, UploadProps } from "antd/es/upload/interface";
import type { UploadChangeParam } from "antd/es/upload";
import type { TabsProps } from "antd";
import { LoadingOutlined, PlusSquareFilled, CloudUploadOutlined, CheckCircleFilled } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { cloneDeep, delay, debounce } from "lodash-es";
import { useSelector } from "react-redux";
import StickyBox from "react-sticky-box";
import api from "@/api";
import { findDeep, changeSize, getSolutionDetail } from "@/utils";
import { appNameReg } from "@/utils/constant";
import { uploadBaseUrl } from "@/api/release";
import ImgCrop from "antd-img-crop";
import ApplyDown from "./ApplyDown";

import dataJson from "@/../public/data.json";

import "@wangeditor/editor/dist/css/style.css";
import { Editor, Toolbar } from "@wangeditor/editor-for-react";
import { IDomEditor, IEditorConfig, IToolbarConfig, Boot } from "@wangeditor/editor";
import attachmentModule from "@wangeditor/plugin-upload-attachment";

// 注册附件插件
Boot.registerModule(attachmentModule);

const prefix = "mySolutionDetail-version_manage";
import "./index.less";
import { getAuthTokenValue } from "@kaihong/utils/lib/khAuthToken";
import coverSampleUrl from "@/assets/images/cover_sample.png";

const ScenarioTags: React.FC<{
  value?: string[];
  options?: any[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
}> = ({ value = [], options = [], onChange, disabled }) => {
  const toggleTag = (tagValue: string) => {
    if (disabled) return;
    const nextValue = value.includes(tagValue) ? value.filter((t) => t !== tagValue) : [...value, tagValue];
    onChange?.(nextValue);
  };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", width: 720 }}>
      {options.map((item) => {
        const checked = value.includes(item.dictCode);
        return (
          <div
            key={item.dictCode}
            onClick={() => toggleTag(item.dictCode)}
            style={{
              padding: "6px 15px",
              border: `1px solid ${checked ? (disabled ? "#78a9ff" : "#0A59F7") : "rgba(0,0,0,0.2)"}`,
              borderRadius: "18px",
              cursor: disabled ? "not-allowed" : "pointer",
              color: checked ? "#fff" : "rgba(0,0,0,0.9)",
              backgroundColor: checked ? (disabled ? "#78a9ff" : "#0A59F7") : disabled ? "#f5f5f5" : "#fff",
              fontSize: "14px",
              userSelect: "none",
              opacity: disabled ? 0.6 : 1,
            }}
          >
            {item.dictLabel}
          </div>
        );
      })}
    </div>
  );
};

const RICH_TEXT_IMAGE_MAX_SIZE = 5 * 1024 * 1024;
const RICH_TEXT_ATTACHMENT_MAX_SIZE = 10 * 1024 * 1024;

const RichTextEditor: React.FC<{
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  uploadUrl?: string;
  placeholder?: string;
}> = ({ value = "", onChange, disabled, uploadUrl, placeholder = "请输入内容..." }) => {
  const [editor, setEditor] = useState<IDomEditor | null>(null);
  const editorRef = useRef<IDomEditor | null>(null);
  const editorWrapperRef = useRef<HTMLDivElement>(null);
  const fullScreenContainerRef = useRef<HTMLElement | null>(null);
  const fullScreenParentRef = useRef<HTMLElement | null>(null);
  const fullScreenNextSiblingRef = useRef<Node | null>(null);
  const disabledRef = useRef(disabled);

  const syncReadonlyMask = useCallback((isReadonly?: boolean) => {
    const readonly = isReadonly ?? !!disabledRef.current;
    const textContainer = editorRef.current?.getEditableContainer() as HTMLElement | null;
    if (!textContainer) return;
    textContainer.classList.toggle("rich-text-editor-text--readonly", readonly);
  }, []);

  const restoreFullScreenContainer = useCallback(() => {
    const container = fullScreenContainerRef.current;
    const parent = fullScreenParentRef.current;
    if (!container || !parent) return;

    if (fullScreenNextSiblingRef.current && fullScreenNextSiblingRef.current.parentElement === parent) {
      parent.insertBefore(container, fullScreenNextSiblingRef.current);
    } else {
      parent.appendChild(container);
    }

    fullScreenContainerRef.current = null;
    fullScreenParentRef.current = null;
    fullScreenNextSiblingRef.current = null;
  }, []);

  const handleEditorFullScreen = useCallback(() => {
    requestAnimationFrame(() => {
      const container = (editorRef.current?.getEditableContainer()?.closest(".w-e-full-screen-container") ??
        editorWrapperRef.current?.closest(".w-e-full-screen-container")) as HTMLElement | null;
      if (!container?.parentElement) return;

      if (container.parentElement !== document.body) {
        fullScreenContainerRef.current = container;
        fullScreenParentRef.current = container.parentElement;
        fullScreenNextSiblingRef.current = container.nextSibling;
        document.body.appendChild(container);
      }

      syncReadonlyMask();
    });
  }, [syncReadonlyMask]);

  const handleEditorUnFullScreen = useCallback(() => {
    restoreFullScreenContainer();
    requestAnimationFrame(() => {
      syncReadonlyMask();
    });
  }, [restoreFullScreenContainer, syncReadonlyMask]);

  const toolbarConfig: Partial<IToolbarConfig> = {
    excludeKeys: [
      "group-video", // 彻底隐藏整个视频菜单组
    ],
    insertKeys: {
      index: 22, // 插入的位置，放在图片/视频附近
      keys: ["uploadAttachment"], // 附件上传按钮的 key
    },
  };
  const editorConfig: Partial<IEditorConfig> = {
    placeholder: placeholder,
    hoverbarKeys: {
      attachment: {
        menuKeys: ["downloadAttachment"], // 点击附件卡片时弹出的操作条
      },
    },
    MENU_CONF: {
      uploadImage: {
        customUpload(file: File, insertFn: (url: string, alt: string, href: string) => void) {
          if (file.size > RICH_TEXT_IMAGE_MAX_SIZE) {
            message.error("图片大小不能超过5M");
            return;
          }
          if (!uploadUrl) {
            message.error("图片上传失败");
            return;
          }

          const formData = new FormData();
          formData.append("file", file);
          formData.append("type", "SOLUTION_CONTENT_IMAGE");

          fetch(uploadUrl, {
            method: "POST",
            headers: {
              Authorization: getAuthTokenValue("Authorization") || "",
            },
            body: formData,
          })
            .then((res) => res.json())
            .then((res) => {
              if (res?.code === 200) {
                const url = res?.data?.url;
                insertFn(url, "", url);
              } else {
                message.error(res?.msg || "图片上传失败");
              }
            })
            .catch(() => {
              message.error("图片上传失败");
            });
        },
      },
      uploadAttachment: {
        customUpload(file: File, insertFn: (fileName: string, url: string) => void) {
          if (file.size > RICH_TEXT_ATTACHMENT_MAX_SIZE) {
            message.error("附件大小不能超过10M");
            return;
          }
          if (!uploadUrl) {
            message.error("附件上传失败");
            return;
          }

          const formData = new FormData();
          formData.append("file", file);
          formData.append("type", "SOLUTION_DOCUMENT_PDF");

          fetch(uploadUrl, {
            method: "POST",
            headers: {
              Authorization: getAuthTokenValue("Authorization") || "",
            },
            body: formData,
          })
            .then((res) => res.json())
            .then((res) => {
              if (res?.code === 200) {
                const url = res?.data?.url;
                insertFn(file.name, url);
              } else {
                message.error(res?.msg || "附件上传失败");
              }
            })
            .catch(() => {
              message.error("附件上传失败");
            });
        },
      },
    },
  };

  useEffect(() => {
    return () => {
      if (editor == null) return;
      editor.off("fullScreen", handleEditorFullScreen);
      editor.off("unFullScreen", handleEditorUnFullScreen);
      restoreFullScreenContainer();
      editor.destroy();
      setEditor(null);
    };
  }, [editor, handleEditorFullScreen, handleEditorUnFullScreen, restoreFullScreenContainer]);

  useEffect(() => {
    disabledRef.current = disabled;
    syncReadonlyMask(!!disabled);
  }, [disabled, syncReadonlyMask]);

  useEffect(() => {
    if (editor) {
      if (disabled) {
        editor.disable();
      } else {
        editor.enable();
      }
      syncReadonlyMask(!!disabled);
    }
  }, [editor, disabled, syncReadonlyMask]);

  return (
    <div
      ref={editorWrapperRef}
      style={{
        border: "1px solid #d9d9d9",
        width: 720,
      }}
    >
      <Toolbar
        editor={editor}
        defaultConfig={toolbarConfig}
        mode="default"
        style={{ borderBottom: "1px solid #d9d9d9" }}
      />
      <Editor
        defaultConfig={editorConfig}
        value={value}
        onCreated={(editor) => {
          setEditor(editor);
          editorRef.current = editor;
          editor.on("fullScreen", handleEditorFullScreen);
          editor.on("unFullScreen", handleEditorUnFullScreen);
          if (disabledRef.current) {
            syncReadonlyMask(true);
          }
        }}
        onChange={(editor) => onChange?.(editor.getHtml())}
        mode="default"
        style={{ height: "350px" }}
      />
    </div>
  );
};

const syncFinishFields = ["coverResources", "backgroundResources", "summary", "industry"];

const isDictCodeInOptions = (dictCode: string | undefined, options: any[]) =>
  !!dictCode && options.some((item) => item.dictCode === dictCode);

const filterValidDictCodes = (codes: string[] | undefined, options: any[]) => {
  if (!Array.isArray(codes) || !options.length) return codes ?? [];
  return codes.filter((code) => options.some((item) => item.dictCode === code));
};

const imageHasTransparency = (img: HTMLImageElement): boolean => {
  if (!img.width || !img.height) {
    return false;
  }
  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return false;
  ctx.drawImage(img, 0, 0);
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true;
  }
  return false;
};

const loadImageFromFile = (file: RcFile): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject();
    };
    img.src = url;
  });

const COVER_UPLOAD_OPTS = {
  width: 480,
  height: 480,
  size: 3145728,
  accept: ["png"],
};

const BACKGROUND_UPLOAD_OPTS = {
  width: 1920,
  height: 700,
  size: 5242880,
  accept: ["png", "jpg", "jpeg"],
};

const VersionManage: React.FC = () => {
  const { type, id, versionId } = useParams();
  const {
    release,
    one_bars,
    two_bars,
    save_text,
    save_success_text,
    req_err,
    cancelText,
    confirmText,
    asterisk,
    my_ai_model,
  } = dataJson;

  const my_solution_detail = dataJson.my_solution_detail as any;

  const [activeKey, setActiveKey] = useState("1");
  const [isBaseFinish, setIsBaseFinish] = useState(false);
  const [isVersionFinish, setIsVersionFinish] = useState(false);

  const [coverLoading, setCoverLoading] = useState(false);
  const [coverImageUrl, setCoverImageUrl] = useState<string>();
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string>();

  const [detailData, setDetailData] = useState<any>();

  const [savaLoading, setSavaLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [cancelReleaseLoading, setCancelReleaseLoading] = useState(false);
  const [cancelDownLoading, setCancelDownLoading] = useState(false);

  const [applyDownVisible, setApplyDownVisible] = useState(false);

  const solutionDetail = useSelector((state: any) => state.solutionDetail);

  const [baseForm] = Form.useForm();
  const baseFormValues = Form.useWatch([], baseForm);

  const [versionForm] = Form.useForm();
  const versionFormValues = Form.useWatch([], versionForm);

  const uploadUrl = `${uploadBaseUrl}/solution/upload/public/${id}`;

  const coverCanUploadRef = useRef(false);
  const backgroundCanUploadRef = useRef(false);
  const lastCoverIdRef = useRef<string>("");
  const lastBackgroundIdRef = useRef<string>("");
  const originalNameRef = useRef("");
  const nameCheckPassedRef = useRef<string | null>(null);
  const nameCheckFailedRef = useRef<string | null>(null);
  const nameCheckSeqRef = useRef(0);
  const nameCheckTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const pendingNameRejectRef = useRef<((reason?: any) => void) | null>(null);
  const autoCoverAttemptedRef = useRef(false);

  const restoreCoverResource = () => {
    coverCanUploadRef.current = false;
    baseForm.setFieldValue("coverResources", lastCoverIdRef.current);
  };

  const restoreBackgroundResource = () => {
    backgroundCanUploadRef.current = false;
    baseForm.setFieldValue("backgroundResources", lastBackgroundIdRef.current);
  };

  const updateBaseFinish = useCallback(() => {
    baseForm
      .validateFields(syncFinishFields, { validateOnly: true })
      .then(() => {
        const name = baseForm.getFieldValue("name");
        if (!name || !appNameReg.test(name)) {
          setIsBaseFinish(false);
          return;
        }
        if (name === nameCheckFailedRef.current) {
          setIsBaseFinish(false);
          return;
        }
        setIsBaseFinish(true);
      })
      .catch(() => setIsBaseFinish(false));
  }, [baseForm]);

  const validateImageBeforeCrop = async (
    file: RcFile,
    { size, accept }: { size: number; accept?: string[] },
    { checkTransparency = true }: { checkTransparency?: boolean } = {}
  ): Promise<boolean> => {
    if (checkTransparency) {
      const coverAccept = accept ?? ["png"];
      const isPng = coverAccept.some((item) => file.type === `image/${item}`);
      if (!isPng) {
        message.error(my_solution_detail.upload_transparent_required);
        return false;
      }
    } else {
      const acceptText = (accept ?? []).join("、").toUpperCase();
      const isAccept = (accept ?? []).some((item) => file.type === `image/${item}`);

      if (!isAccept) {
        message.error(
          `${my_solution_detail.upload_format_err_text1}${acceptText}${my_solution_detail.upload_format_err_text2}`
        );
        return false;
      }
    }

    if (file.size > size) {
      const sizeText = changeSize(size);
      message.error(`${release.upload_size_err.text1}${sizeText}${release.upload_size_err.text2}`);
      return false;
    }

    if (!checkTransparency) {
      return true;
    }

    try {
      const img = await loadImageFromFile(file);
      if (!imageHasTransparency(img)) {
        message.error(my_solution_detail.upload_transparent_required);
        return false;
      }
      return true;
    } catch {
      message.error(release.upload_fail);
      return false;
    }
  };

  const renderTabBar: TabsProps["renderTabBar"] = (props, DefaultTabBar) => (
    <StickyBox offsetTop={0} offsetBottom={20} style={{ zIndex: 99, background: "#fff", paddingTop: 24 }}>
      <DefaultTabBar {...props} />
    </StickyBox>
  );

  const onChange = (key: string) => {
    setActiveKey(key);
  };

  const renderBtn = (item: string) => {
    const newShowMode = detailData?.versions?.length > 1 && detailData?.versions[0]?.showMode === "AUDIT_UPGRADE";
    switch (item) {
      case "save":
        return (
          <Button type="primary" loading={savaLoading} ghost style={{ marginLeft: "16px" }} onClick={handleSave}>
            {save_text}
          </Button>
        );
      case "submitReview":
        return (
          <Button type="primary" loading={submitLoading} style={{ marginLeft: "16px" }} onClick={submitReview}>
            {release.submit_review}
          </Button>
        );
      case "cancelRelease":
        return (
          <Button type="primary" style={{ marginLeft: "16px" }} onClick={cancelRelease}>
            {release.cancel_release}
          </Button>
        );
      case "down":
        if (newShowMode) {
          break;
        }
        return (
          <Button
            type="primary"
            ghost
            style={{ marginLeft: "16px" }}
            onClick={() => {
              setApplyDownVisible(true);
            }}
          >
            {release.audit_down}
          </Button>
        );
      case "cancelDown":
        return (
          <Button type="primary" style={{ marginLeft: "16px" }} onClick={cancelDown}>
            {release.cancel_down}
          </Button>
        );
      default:
        return;
    }
  };

  const validateName = (_: any, value: any) => {
    if (!value) {
      return Promise.resolve();
    }
    if (value === originalNameRef.current || value === nameCheckPassedRef.current) {
      return Promise.resolve();
    }
    if (pendingNameRejectRef.current) {
      pendingNameRejectRef.current();
      pendingNameRejectRef.current = null;
    }
    return new Promise((resolve, reject) => {
      pendingNameRejectRef.current = reject;
      if (nameCheckTimerRef.current) {
        clearTimeout(nameCheckTimerRef.current);
      }
      nameCheckTimerRef.current = setTimeout(() => {
        pendingNameRejectRef.current = null;
        const seq = ++nameCheckSeqRef.current;
        api
          .existSolutionName({ name: value, id })
          .then((resp: any) => {
            if (seq !== nameCheckSeqRef.current) return;
            if (value !== baseForm.getFieldValue("name")) return;
            if (resp.code === 200) {
              if (resp.data?.exist) {
                nameCheckPassedRef.current = null;
                nameCheckFailedRef.current = value;
                setIsBaseFinish(false);
                reject(my_ai_model.name_already);
              } else {
                nameCheckFailedRef.current = null;
                nameCheckPassedRef.current = value;
                updateBaseFinish();
                resolve(undefined);
              }
            } else {
              reject(resp.msg || req_err);
            }
          })
          .catch(() => {
            if (seq !== nameCheckSeqRef.current) return;
            if (value !== baseForm.getFieldValue("name")) return;
            reject(req_err);
          });
      }, 500);
    });
  };

  const assertSolutionNameUnique = async (name: string): Promise<boolean> => {
    try {
      const resp: any = await api.existSolutionName({ name, id });
      if (resp.code === 200) {
        if (resp.data?.exist) {
          nameCheckPassedRef.current = null;
          nameCheckFailedRef.current = name;
          setIsBaseFinish(false);
          setActiveKey("1");
          baseForm.setFields([{ name: "name", errors: [my_ai_model.name_already] }]);
          scrollErrDom(".ant-form-item-has-error", 300);
          return false;
        }
        nameCheckFailedRef.current = null;
        nameCheckPassedRef.current = name;
        return true;
      }
      message.error(resp.msg || req_err);
      return false;
    } catch {
      message.error(req_err);
      return false;
    }
  };

  const scrollErrDom = (domClass: string, delayTime?: number) => {
    const scrollToFirstError = () => {
      const errorList = document.querySelectorAll(domClass);
      const uploadError = document.querySelector(
        ".ig-cover-item.ant-form-item-has-error, .ig-bg-item.ant-form-item-has-error"
      );
      (uploadError ?? errorList[0])?.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    };
    if (delayTime) {
      delay(scrollToFirstError, delayTime);
    } else {
      scrollToFirstError();
    }
  };

  // 处理请求参数
  const handleReq = async (isValidate: boolean) => {
    if (coverLoading || backgroundLoading) {
      message.warning(release.upload_img_ing);
      return;
    }

    let baseValues: any;
    let versionValues: any;

    if (isValidate) {
      if (activeKey === "1") {
        try {
          baseValues = await baseForm.validateFields();
        } catch (error) {
          scrollErrDom(".ant-form-item-has-error", 100);
          return;
        }

        if (baseValues) {
          try {
            versionValues = await versionForm.validateFields();
          } catch (error) {
            setActiveKey("2");
            scrollErrDom(".ant-form-item-has-error", 300);
            return;
          }
        }
      } else {
        try {
          baseValues = await baseForm.validateFields();
        } catch (error) {
          setActiveKey("1");
          scrollErrDom(".ant-form-item-has-error", 300);
          return;
        }
        if (baseValues) {
          try {
            versionValues = await versionForm.validateFields();
          } catch (error) {
            scrollErrDom(".ant-form-item-has-error", 100);
            return;
          }
        }
      }
    } else {
      const currentName = baseForm.getFieldValue("name");

      if (!currentName) {
        setActiveKey("1");
        baseForm.validateFields(["name"]);
        scrollErrDom(".ant-form-item-has-error", 300);
        return;
      }

      try {
        await baseForm.validateFields(["name"]);
      } catch (error) {
        setActiveKey("1");
        scrollErrDom(".ant-form-item-has-error", 300);
        return;
      }

      baseValues = await baseForm.getFieldsValue();
      versionValues = await versionForm.getFieldsValue();
    }

    if (baseValues?.name && !(await assertSolutionNameUnique(baseValues.name))) {
      return;
    }

    if (isValidate) {
      const dictResult = await assertSolutionDictFields(baseValues, versionValues);
      if (!dictResult.ok) {
        return;
      }
      baseValues = dictResult.baseValues ?? baseValues;
      versionValues = dictResult.versionValues ?? versionValues;
    }

    try {
      const resourcesArr = [];
      const coverIds = baseValues?.coverResources;
      !!coverIds &&
        resourcesArr.push({
          resourceIds: [coverIds],
          type: "SOLUTION_COVER",
        });

      const backgroundIds = baseValues?.backgroundResources;
      !!backgroundIds &&
        resourcesArr.push({
          resourceIds: [backgroundIds],
          type: "SOLUTION_BACKGROUND",
        });

      const req = {
        id,
        versionId,
        name: baseValues?.name,
        summary: baseValues?.summary,
        industryId: baseValues?.industry,
        resources: resourcesArr,
        sceneTags: versionValues?.trialScenario,
        content: versionValues?.content,
      };
      return req;
    } catch (errorInfo: any) {
      console.error(errorInfo);
      return;
    }
  };

  const handleSave = async () => {
    try {
      setSavaLoading(true);
      const req: any = await handleReq(false);
      if (req) {
        try {
          const res: any = await api.saveSolution(req);
          if (res?.code == 200) {
            getSolutionDetail({ id, versionId });
            message.success(save_success_text);
          } else {
            message.error(res.msg);
          }
        } catch (errorInfo: any) {
          message.error(errorInfo?.response?.data?.msg || req_err);
        }
      }
      setSavaLoading(false);
    } catch (error) {
      setSavaLoading(false);
      console.log(error);
    }
  };

  // 提交审核
  const submitReview = debounce(async () => {
    const req = await handleReq(true);

    if (req) {
      try {
        setSubmitLoading(true);
        const res: any = await api.saveNextSolution(req);
        setSubmitLoading(false);
        if (res?.code == 200) {
          // 接口要区分是否是升级的提交
          let res2: any = null;
          res2 = await api.auditReleaseSolution({ versionId });
          if (res2?.code == 200) {
            message.success(release.wait_review);
            getSolutionDetail({ id, versionId });
          } else {
            message.error(res2.msg);
          }
        } else {
          message.error(res.msg);
        }
      } catch (errorInfo: any) {
        setSubmitLoading(false);
        message.error(errorInfo?.response?.data?.msg || req_err);
      }
    }
  }, 300);

  const cancelRelease = async () => {
    const modal = Modal.confirm({
      icon: null,
      centered: true,
      closable: true,
      width: 350,
      title: release.cancel_release_title,
      content: release.cancel_release_content,
      footer: (
        <div style={{ textAlign: "right", marginTop: "20px" }}>
          <Button
            type="primary"
            ghost
            onClick={() => {
              modal.destroy();
            }}
          >
            {cancelText}
          </Button>
          <Button
            type="primary"
            style={{ marginLeft: "24px" }}
            loading={cancelReleaseLoading}
            onClick={async () => {
              try {
                setCancelReleaseLoading(true);
                let res: any = null;
                res = await api.cancelReleaseSolution({ versionId });
                setCancelReleaseLoading(false);
                if (res?.code == 200) {
                  message.success(release.cancel_release_success);
                  getSolutionDetail({ id, versionId });
                  modal.destroy();
                } else {
                  message.error(res.msg);
                }
              } catch (errorInfo: any) {
                setCancelReleaseLoading(false);
                message.error(errorInfo?.response?.data?.msg || req_err);
              }
            }}
          >
            {confirmText}
          </Button>
        </div>
      ),
    });
  };

  const cancelDown = async () => {
    const modal = Modal.confirm({
      icon: null,
      centered: true,
      closable: true,
      width: 350,
      title: release.cancel_down,
      content: release.cancel_down_content,
      footer: (
        <div style={{ textAlign: "right", marginTop: "20px" }}>
          <Button
            type="primary"
            ghost
            onClick={() => {
              modal.destroy();
            }}
          >
            {cancelText}
          </Button>
          <Button
            type="primary"
            loading={cancelDownLoading}
            style={{ marginLeft: "24px" }}
            onClick={async () => {
              try {
                setCancelDownLoading(true);
                const res: any = await api.cancelDownSolution({ versionId });
                setCancelDownLoading(false);
                if (res?.code == 200) {
                  message.success(release.cancel_down_success);
                  getSolutionDetail({ id, versionId });
                  modal.destroy();
                } else {
                  message.error(res.msg);
                }
              } catch (errorInfo: any) {
                setCancelDownLoading(false);
                message.error(errorInfo?.response?.data?.msg || req_err);
              }
            }}
          >
            {confirmText}
          </Button>
        </div>
      ),
    });
  };

  const applyDownClose = () => {
    setApplyDownVisible(false);
    getSolutionDetail({ id, versionId });
  };

  // 初始化数据
  const initHandle = (data: any) => {
    const versionDataCopy = cloneDeep(data?.currentVersionDetail);

    const coverItem = versionDataCopy?.resources?.find((item: any) => item.type == "SOLUTION_COVER");
    const backgroundItem = versionDataCopy?.resources?.find((item: any) => item.type == "SOLUTION_BACKGROUND");

    const coverId = coverItem?.id || "";
    const backgroundId = backgroundItem?.id || "";

    lastCoverIdRef.current = coverId;
    lastBackgroundIdRef.current = backgroundId;
    originalNameRef.current = versionDataCopy?.title || "";
    nameCheckPassedRef.current = null;
    nameCheckFailedRef.current = null;

    baseForm.setFieldsValue({
      coverResources: coverId,
      backgroundResources: backgroundId,
      name: versionDataCopy?.title || "",
      summary: versionDataCopy?.summary || "",
      industry: versionDataCopy?.industryId || undefined,
    });

    let trialScenario = versionDataCopy?.sceneTags;
    if (typeof trialScenario === "string") {
      try {
        trialScenario = JSON.parse(trialScenario);
      } catch (e) {
        trialScenario = trialScenario.split(",").filter(Boolean);
      }
    }

    versionForm.setFieldsValue({
      trialScenario: trialScenario,
      content: versionDataCopy?.content,
    });
    setCoverImageUrl(coverItem?.url);
    setBackgroundImageUrl(backgroundItem?.url);

    // 主动触发一次校验，更新绿勾状态
    updateBaseFinish();

    const currentTrialScenario = versionForm.getFieldValue("trialScenario");
    const currentContent = versionForm.getFieldValue("content");
    const isTrialValid = Array.isArray(currentTrialScenario) && currentTrialScenario.length > 0;
    const isContentValid = !!currentContent && currentContent !== "<p><br></p>";
    setIsVersionFinish(isTrialValid && isContentValid);
  };

  // 图片上传前处理
  const beforeUpload: any = (file: RcFile, { width, height, size, accept = ["png"], exportType }: any) => {
    return new Promise<File | void>((resolve, reject) => {
      const acceptText = accept.join("、").toUpperCase();

      const _URL = window.URL || window.webkitURL;
      const img = new Image();
      img.src = _URL.createObjectURL(file);

      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        message.error(release.upload_fail);
        reject();
      };
      img.onload = () => {
        const objectUrl = img.src;
        const outputType =
          exportType ?? (file.type === "image/jpeg" || file.type === "image/jpg" ? "image/jpeg" : "image/png");

        // 尺寸已符合则跳过 Canvas 重绘，避免透明通道被二次编码丢失
        if (img.width === width && img.height === height) {
          URL.revokeObjectURL(objectUrl);
          if (size && file.size > size) {
            const sizeText = changeSize(size);
            message.error(`${release.upload_size_err.text1}${sizeText}${release.upload_size_err.text2}`);
            reject();
          } else {
            resolve(file);
          }
          return;
        }

        // 利用 Canvas 强行缩放到指定分辨率
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d", { alpha: true });
        if (ctx) {
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          URL.revokeObjectURL(objectUrl);
          canvas.toBlob((blob) => {
            if (blob) {
              if (size && blob.size > size) {
                const sizeText = changeSize(size);
                message.error(`${release.upload_size_err.text1}${sizeText}${release.upload_size_err.text2}`);
                reject();
              } else {
                const newFile = new File([blob], file.name, { type: outputType, lastModified: Date.now() });
                resolve(newFile);
              }
            } else {
              message.error(release.upload_fail);
              reject();
            }
          }, outputType);
        } else {
          URL.revokeObjectURL(objectUrl);
          // 如果 canvas 获取失败，回退到原有的严格校验逻辑
          if (img.width != width || img.height != height) {
            message.error(
              `${release.upload_accept_err.text1}${width}${asterisk}${height}${release.upload_accept_err.text2}${acceptText}${release.upload_accept_err.text3}`
            );
            reject();
          } else {
            resolve();
          }
        }
      };
    });
  };

  const ensureCoverFileHasTransparency = async (file: RcFile | File): Promise<boolean> => {
    try {
      const img = await loadImageFromFile(file as RcFile);
      if (!imageHasTransparency(img)) {
        message.error(my_solution_detail.upload_transparent_required);
        return false;
      }
      return true;
    } catch {
      message.error(release.upload_fail);
      return false;
    }
  };

  const processCoverUploadFile = async (file: RcFile): Promise<File | null> => {
    let processed: File | void;
    try {
      processed = await beforeUpload(file, COVER_UPLOAD_OPTS);
    } catch {
      return null;
    }
    if (!processed) {
      return null;
    }
    const hasTransparency = await ensureCoverFileHasTransparency(processed);
    if (!hasTransparency) {
      return null;
    }
    return processed;
  };

  // 封面新增
  const applyCoverUploadResult = (iconUrl: string, iconId: string) => {
    lastCoverIdRef.current = iconId;
    setCoverImageUrl(iconUrl);
    baseForm.setFieldValue("coverResources", iconId);
    baseForm.validateFields(["coverResources"]);
  };

  const uploadCoverResource = async (file: RcFile) => {
    const valid = await validateImageBeforeCrop(file, COVER_UPLOAD_OPTS);
    if (!valid) {
      return false;
    }

    const uploadFile = await processCoverUploadFile(file);
    if (!uploadFile) {
      return false;
    }

    setCoverLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("type", "SOLUTION_COVER");

      const res: any = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          Authorization: getAuthTokenValue("Authorization") || "",
        },
        body: formData,
      }).then((response) => response.json());

      if (res?.code === 200) {
        applyCoverUploadResult(res?.data?.url, res?.data?.id);
        return true;
      }
      message.error(res?.msg || req_err);
      return false;
    } catch {
      message.error(req_err);
      return false;
    } finally {
      setCoverLoading(false);
    }
  };

  const autoUploadDefaultCover = async () => {
    if (autoCoverAttemptedRef.current) {
      return;
    }
    autoCoverAttemptedRef.current = true;

    try {
      const response = await fetch(coverSampleUrl);
      const blob = await response.blob();
      const file = new File([blob], "cover_sample.png", {
        type: blob.type || "image/png",
        lastModified: Date.now(),
      }) as RcFile;
      await uploadCoverResource(file);
    } catch {
      message.error(release.upload_fail);
    }
  };

  const coverImageChange: UploadProps["onChange"] = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === "uploading") {
      setCoverLoading(true);
      return;
    }
    setCoverLoading(false);

    if (info.file.status === "done") {
      if (info.file.response.code === 200) {
        applyCoverUploadResult(info?.file?.response?.data?.url, info?.file?.response?.data?.id);
      } else {
        message.error(info.file.response.msg);
        restoreCoverResource();
      }
    } else if (info.file.status === "error") {
      restoreCoverResource();
    }
  };

  // 背景新增
  const backgroundImageChange: UploadProps["onChange"] = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === "uploading") {
      setBackgroundLoading(true);
      return;
    }
    setBackgroundLoading(false);

    if (info.file.status === "done") {
      if (info.file.response.code === 200) {
        const iconUrl = info?.file?.response?.data?.url;
        const iconId = info?.file?.response?.data?.id;

        lastBackgroundIdRef.current = iconId;
        setBackgroundImageUrl(iconUrl);
        baseForm.setFieldValue("backgroundResources", iconId);
      } else {
        message.error(info.file.response.msg);
        restoreBackgroundResource();
      }
    } else if (info.file.status === "error") {
      restoreBackgroundResource();
    }

    baseForm.validateFields(["backgroundResources"]);
  };

  const getOptions = async (type: string) => {
    try {
      const res: any = await api.getNewDict(type);
      if (res?.code == 200) {
        const arr = res.data?.[type].filter((item: any) => item.dictValue !== "ALL" && item.dictValue !== "ALL_DOMAIN");
        return arr;
      } else {
        message.error(res.msg);
        return [];
      }
    } catch (errorInfo: any) {
      message.error(errorInfo?.response?.data?.msg || req_err);
      return [];
    }
  };

  const [industryOption, setIndustryOption] = useState<any[]>([]);
  const [sceneOption, setSceneOption] = useState<any[]>([]);

  const syncStaleDictFields = useCallback(() => {
    let changed = false;

    if (industryOption.length) {
      const industryId = baseForm.getFieldValue("industry");
      if (industryId && !isDictCodeInOptions(industryId, industryOption)) {
        baseForm.setFieldValue("industry", undefined);
        changed = true;
      }
    }

    if (sceneOption.length) {
      const trialScenario = versionForm.getFieldValue("trialScenario");
      if (Array.isArray(trialScenario) && trialScenario.length) {
        const validTags = filterValidDictCodes(trialScenario, sceneOption);
        if (validTags.length !== trialScenario.length) {
          versionForm.setFieldValue("trialScenario", validTags.length ? validTags : undefined);
          changed = true;
        }
      }
    }

    if (changed) {
      updateBaseFinish();
      const currentTrialScenario = versionForm.getFieldValue("trialScenario");
      const currentContent = versionForm.getFieldValue("content");
      const isTrialValid = Array.isArray(currentTrialScenario) && currentTrialScenario.length > 0;
      const isContentValid = !!currentContent && currentContent !== "<p><br></p>";
      setIsVersionFinish(isTrialValid && isContentValid);
    }
  }, [baseForm, versionForm, industryOption, sceneOption, updateBaseFinish]);

  const assertSolutionDictFields = async (
    baseValues: any,
    versionValues: any
  ): Promise<{ ok: boolean; baseValues?: any; versionValues?: any }> => {
    const { domain, topic } = my_solution_detail.dict;
    const [industryList, sceneList] = await Promise.all([getOptions(domain), getOptions(topic)]);

    setIndustryOption(industryList);
    setSceneOption(sceneList);

    let hasError = false;
    let nextBaseValues = baseValues;
    let nextVersionValues = versionValues;

    const industryId = baseValues?.industry;
    if (industryId && industryList.length && !isDictCodeInOptions(industryId, industryList)) {
      baseForm.setFieldValue("industry", undefined);
      baseForm.setFields([{ name: "industry", errors: [my_solution_detail.industry_label_placeholder] }]);
      setIsBaseFinish(false);
      setActiveKey("1");
      nextBaseValues = { ...baseValues, industry: undefined };
      hasError = true;
    }

    const trialScenario = versionValues?.trialScenario;
    if (Array.isArray(trialScenario) && trialScenario.length && sceneList.length) {
      const validTags = filterValidDictCodes(trialScenario, sceneList);
      if (validTags.length !== trialScenario.length) {
        // 部分失效：只提交仍有效的场景，不拦截
        // 全部失效：清空并拦截
        versionForm.setFieldValue("trialScenario", validTags.length ? validTags : undefined);
        nextVersionValues = { ...versionValues, trialScenario: validTags.length ? validTags : undefined };

        if (!validTags.length) {
          versionForm.setFields([{ name: "trialScenario", errors: ["请选择试用场景"] }]);
          setIsVersionFinish(false);
          if (!hasError) {
            setActiveKey("2");
          }
          hasError = true;
        }
      }
    }

    if (hasError) {
      scrollErrDom(".ant-form-item-has-error", 300);
      return { ok: false };
    }

    return { ok: true, baseValues: nextBaseValues, versionValues: nextVersionValues };
  };

  const domainAndTopic = async () => {
    const { domain, topic } = my_solution_detail.dict;

    const industryList: any = await getOptions(domain);
    setIndustryOption(industryList);

    const sceneList: any = await getOptions(topic);
    setSceneOption(sceneList);
  };

  useEffect(() => {
    autoCoverAttemptedRef.current = false;
  }, [id, versionId]);

  useEffect(() => {
    if (!industryOption.length && !sceneOption.length) return;
    syncStaleDictFields();
  }, [industryOption, sceneOption, syncStaleDictFields]);

  useEffect(() => {
    if (solutionDetail) {
      const resData = cloneDeep(solutionDetail);

      const resShowMode = findDeep(release.show_mode_arr, resData?.currentVersionDetail?.showMode);
      const nowIndex = resData?.versions.findIndex((item: any) => item.id == resData.currentVersionDetail.id);
      if (nowIndex === 0) {
        //最新版本
        if (resShowMode?.isUpgrade && resData?.versions?.length > 1) {
          // 有升级场景，在新版本页面，按钮要受到旧版本状态影响
          const oldShowMode = resData?.versions[1]?.showMode;
          resData.baseBtns = resShowMode?.btnsByOld[oldShowMode];
        } else {
          resData.baseBtns = resShowMode?.baseBtns;
        }
      } else if (nowIndex === 1) {
        //旧版本
        if (resShowMode?.btnsByNew && resData?.versions?.length > 1) {
          // 有升级场景，在旧版本页面，按钮要受到新版本状态影响
          resData.baseBtns = resShowMode?.btnsByNew[resShowMode];
        } else {
          resData.baseBtns = resShowMode?.baseBtns;
        }
      } else {
        // 历史版本
        resData.baseBtns = resShowMode?.baseBtns;
      }

      resData.showModeLabel = resShowMode?.title;
      resData.isUpgrade = !!resShowMode?.isUpgrade;

      if (resData?.currentVersionDetail?.mode === "DRAFT") {
        resData.showModeLabel = my_solution_detail.new_version + one_bars + resShowMode?.title;
      } else {
        resData.showModeLabel = resData?.currentVersionDetail?.version + one_bars + resShowMode?.title;
      }
      // 当存在两个版本时候，旧版本都不可修改
      // 准备提交
      if (resShowMode.value === "UPGRADING") {
        resData.disabled = false;
      } else if (resData?.versions?.length > 1 && resData?.versions[1]?.id === resData.currentVersionDetail.id) {
        resData.disabled = true;
      } else {
        resData.disabled = resShowMode?.formDisabled;
      }

      resData.opinion = resShowMode?.opinion;

      resData.titleDescriptionLabel = resShowMode?.titleDescription
        ? resShowMode?.pleaseWrite
          ? resShowMode?.pleaseWrite + my_solution_detail.detail_title + resShowMode?.titleDescription
          : my_solution_detail.detail_title + resShowMode?.titleDescription
        : "";
      setDetailData(resData);
      initHandle(resData);
      syncStaleDictFields();

      const coverItem = resData?.currentVersionDetail?.resources?.find((item: any) => item.type == "SOLUTION_COVER");
      if (!coverItem?.id && !resData.disabled) {
        autoUploadDefaultCover();
      }
    }
  }, [solutionDetail]);

  useEffect(() => {
    updateBaseFinish();
  }, [baseFormValues, updateBaseFinish]);

  useEffect(() => {
    const trialScenario = versionForm.getFieldValue("trialScenario");
    const content = versionForm.getFieldValue("content");

    const isTrialScenarioValid = Array.isArray(trialScenario) && trialScenario.length > 0;
    const isContentValid = !!content && content !== "<p><br></p>";

    if (isTrialScenarioValid && isContentValid) {
      setIsVersionFinish(true);
    } else {
      setIsVersionFinish(false);
    }
  }, [versionFormValues, versionForm]);

  useEffect(() => {
    domainAndTopic();
  }, []);

  return (
    <div className={`appDetail-content ${prefix}`}>
      <div className={`${prefix}-header`}>
        <div className={`${prefix}-header-left`}>
          <div title={detailData?.showModeLabel} className="one-line-ellipsis">
            {detailData?.showModeLabel}
          </div>
          {detailData?.titleDescriptionLabel ? <div>{detailData?.titleDescriptionLabel}</div> : null}

          <div className="one-line-ellipsis" title={detailData?.opinion || detailData?.audit?.content}>
            <span>{release.review_comments}</span>
            {detailData?.opinion || detailData?.currentVersionDetail?.rejectReason || two_bars}
          </div>
        </div>
        <div>
          {detailData?.baseBtns?.map((item: string) => {
            return <span key={item}>{renderBtn(item)}</span>;
          })}
        </div>
      </div>
      <div className={`${prefix}-content`}>
        <Tabs
          activeKey={activeKey}
          renderTabBar={renderTabBar}
          indicator={{ size: (origin) => origin - 19, align: "start" }}
          onChange={onChange}
          className={`${prefix}-tabs`}
          items={[
            {
              key: release.tabs[0].key,
              forceRender: true,
              label: (
                <>
                  {release.tabs[0].label}
                  {<CheckCircleFilled style={{ color: isBaseFinish ? "#3EC167" : "#8C8C8C", marginLeft: 4 }} />}
                </>
              ),
              children: (
                <Form
                  disabled={detailData?.disabled}
                  form={baseForm}
                  layout="vertical"
                  scrollToFirstError={true}
                  className={`${prefix}-baseForm ${prefix}-form`}
                >
                  <div style={{ display: "flex", gap: "48px", marginBottom: "24px" }}>
                    <Form.Item
                      label={my_solution_detail.upload_cover}
                      name="coverResources"
                      rules={[{ required: true, message: my_solution_detail.upload_cover_required }]}
                      className={`${prefix}-baseForm-coverResources ig-cover-item`}
                      style={{ marginBottom: 0 }}
                    >
                      <div className="ig-upload-wrapper">
                        <div className={`${prefix}-form-tip`}>{my_solution_detail.upload_cover_tip}</div>
                        <ImgCrop
                          aspect={480 / 480}
                          quality={1}
                          zoomSlider={false}
                          minZoom={1}
                          maxZoom={1}
                          fillColor="rgba(0,0,0,0)"
                          beforeCrop={async (file) => {
                            const valid = await validateImageBeforeCrop(file, COVER_UPLOAD_OPTS);
                            coverCanUploadRef.current = valid;
                            return valid;
                          }}
                          onModalCancel={restoreCoverResource}
                        >
                          <Upload
                            accept=".png"
                            listType="picture-card"
                            showUploadList={false}
                            disabled={detailData?.disabled || coverLoading}
                            maxCount={1}
                            action={uploadUrl}
                            headers={{
                              Authorization: getAuthTokenValue("Authorization") || "",
                            }}
                            data={{
                              type: "SOLUTION_COVER",
                            }}
                            beforeUpload={async (file) => {
                              if (!coverCanUploadRef.current) {
                                return Upload.LIST_IGNORE;
                              }
                              const processed = await processCoverUploadFile(file);
                              if (!processed) {
                                coverCanUploadRef.current = false;
                                return Upload.LIST_IGNORE;
                              }
                              return processed;
                            }}
                            onChange={coverImageChange}
                            style={{ margin: 0, display: "block" }}
                          >
                            {coverImageUrl ? (
                              <img
                                src={coverImageUrl}
                                alt="cover"
                                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "4px" }}
                              />
                            ) : coverLoading ? (
                              <LoadingOutlined />
                            ) : (
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  position: "relative",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <CloudUploadOutlined
                                  className={`${prefix}-add-icon`}
                                  style={{ fontSize: "32px", color: "#8c8c8c" }}
                                />
                                <PlusSquareFilled
                                  style={{
                                    position: "absolute",
                                    bottom: 5,
                                    right: 5,
                                    fontSize: "24px",
                                    color: "#5585e4",
                                    backgroundColor: "#fff",
                                  }}
                                />
                              </div>
                            )}
                          </Upload>
                        </ImgCrop>
                      </div>
                    </Form.Item>
                    <Form.Item
                      label={my_solution_detail.upload_background}
                      name="backgroundResources"
                      rules={[{ required: true, message: my_solution_detail.upload_background_required }]}
                      className={`${prefix}-baseForm-backgroundResources ig-bg-item`}
                      style={{ marginBottom: 0 }}
                    >
                      <div className="ig-upload-wrapper">
                        <div className={`${prefix}-form-tip`}>{my_solution_detail.upload_background_tip}</div>
                        <ImgCrop
                          aspect={1920 / 700}
                          quality={1}
                          fillColor="rgba(0,0,0,0)"
                          beforeCrop={async (file) => {
                            const valid = await validateImageBeforeCrop(file, BACKGROUND_UPLOAD_OPTS, {
                              checkTransparency: false,
                            });
                            backgroundCanUploadRef.current = valid;
                            return valid;
                          }}
                          onModalCancel={restoreBackgroundResource}
                        >
                          <Upload
                            accept=".jpg,.jpeg,.png"
                            listType="picture-card"
                            showUploadList={false}
                            disabled={detailData?.disabled || backgroundLoading}
                            maxCount={1}
                            action={uploadUrl}
                            headers={{
                              Authorization: getAuthTokenValue("Authorization") || "",
                            }}
                            data={{
                              type: "SOLUTION_BACKGROUND",
                            }}
                            beforeUpload={async (file) => {
                              if (!backgroundCanUploadRef.current) {
                                return Upload.LIST_IGNORE;
                              }
                              return beforeUpload(file, BACKGROUND_UPLOAD_OPTS);
                            }}
                            onChange={backgroundImageChange}
                            style={{ margin: 0, display: "block" }}
                          >
                            {backgroundImageUrl ? (
                              <img
                                src={backgroundImageUrl}
                                alt="background"
                                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "4px" }}
                              />
                            ) : backgroundLoading ? (
                              <LoadingOutlined />
                            ) : (
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  position: "relative",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <CloudUploadOutlined
                                  className={`${prefix}-add-icon`}
                                  style={{ fontSize: "32px", color: "#8c8c8c" }}
                                />
                                <PlusSquareFilled
                                  style={{
                                    position: "absolute",
                                    bottom: 5,
                                    right: 5,
                                    fontSize: "24px",
                                    color: "#5585e4",
                                    backgroundColor: "#fff",
                                  }}
                                />
                              </div>
                            )}
                          </Upload>
                        </ImgCrop>
                      </div>
                    </Form.Item>
                  </div>
                  <Form.Item
                    label={my_solution_detail.plugin_text + release.name_label}
                    name="name"
                    rules={[
                      { required: true, message: my_solution_detail.name_placeholder },
                      {
                        pattern: appNameReg,
                        message: my_solution_detail.name_validator,
                      },
                      { validator: validateName },
                    ]}
                    getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
                  >
                    <Input
                      placeholder={`${my_solution_detail.name_placeholder}`}
                      showCount
                      maxLength={30}
                      style={{ width: 720 }}
                    />
                  </Form.Item>
                  <Form.Item
                    label={my_solution_detail.industry_label}
                    name="industry"
                    rules={[{ required: true, message: my_solution_detail.industry_label_placeholder }]}
                  >
                    <Select
                      options={industryOption}
                      placeholder={my_solution_detail.industry_label_placeholder}
                      showSearch
                      fieldNames={{ label: "dictLabel", value: "dictCode" }}
                      filterOption={(input, option) =>
                        (option?.dictLabel ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      style={{ width: 720 }}
                    />
                  </Form.Item>
                  <Form.Item
                    label={my_solution_detail.summary_label}
                    name="summary"
                    required
                    validateDebounce={500}
                    rules={[{ required: true, message: release.introduction_required }]}
                    getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
                  >
                    <Input.TextArea
                      placeholder={release.introduction_required}
                      showCount
                      maxLength={300}
                      style={{ width: 720, height: 128, resize: "none" }}
                    />
                  </Form.Item>
                </Form>
              ),
            },
            {
              key: release.tabs[1].key,
              forceRender: true,
              label: (
                <>
                  {release.tabs[1].label}
                  {<CheckCircleFilled style={{ color: isVersionFinish ? "#3EC167" : "#8C8C8C", marginLeft: 4 }} />}
                </>
              ),
              children: (
                <Form
                  form={versionForm}
                  disabled={detailData?.disabled}
                  layout="vertical"
                  scrollToFirstError={true}
                  className={`${prefix}-versionForm ${prefix}-form`}
                >
                  <Form.Item
                    label="适用场景"
                    name="trialScenario"
                    validateDebounce={500}
                    rules={[{ required: true, type: "array", message: "请选择适用场景" }]}
                  >
                    <ScenarioTags options={sceneOption} disabled={detailData?.disabled} />
                  </Form.Item>
                  <Form.Item
                    label="内容"
                    name="content"
                    validateDebounce={500}
                    required
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value || value === "<p><br></p>") {
                            return Promise.reject(new Error("请输入内容"));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    {uploadUrl ? (
                      <RichTextEditor
                        disabled={detailData?.disabled}
                        uploadUrl={uploadUrl}
                        placeholder={
                          "- 方案描述\n- 核心能力\n- 架构图：展示解决方案整体架构\n- 方案 PDF：上传方案说明文档\n- 部署拓扑：说明部署方式与网络拓扑\n- 实景图片：展示解决方案实际应用场景图片"
                        }
                      />
                    ) : (
                      <div
                        style={{
                          height: 300,
                          border: "1px solid #d9d9d9",
                          borderRadius: 6,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <LoadingOutlined style={{ fontSize: 24, color: "#1890ff" }} />
                      </div>
                    )}
                  </Form.Item>
                </Form>
              ),
            },
          ]}
        />
      </div>
      <ApplyDown type={type} id={id} visible={applyDownVisible} versionId={versionId} close={applyDownClose} />
    </div>
  );
};

export default VersionManage;
